import React, { useState, useMemo } from 'react';
import { AgendaEntry, Teacher, SchoolSettings, View } from '../types';
import { getThemeById } from '../src/themeConfig';
import { 
  BookMarked, 
  Search, 
  Filter, 
  Calendar, 
  User, 
  Building2, 
  BookOpen, 
  CheckCircle2, 
  Clock, 
  AlertCircle, 
  FileSpreadsheet, 
  Printer, 
  ExternalLink, 
  Image as ImageIcon, 
  Trash2, 
  Eye, 
  X, 
  RotateCcw,
  LayoutGrid,
  List,
  CheckSquare,
  Square
} from 'lucide-react';
import ExcelJS from 'exceljs';

interface Props {
  agendas: AgendaEntry[];
  teachers: Teacher[];
  classes: string[];
  schoolSettings: SchoolSettings;
  onNavigate: (view: View, params?: any) => void;
  onDeleteAgenda?: (agendaId: string) => void;
  onUpdateAgenda?: (updated: AgendaEntry) => void;
  appTheme?: string;
}

const AdminAgendaView: React.FC<Props> = ({
  agendas = [],
  teachers = [],
  classes = [],
  schoolSettings,
  onNavigate,
  onDeleteAgenda,
  onUpdateAgenda,
  appTheme = 'wzc-themes',
}) => {
  const currentTheme = useMemo(() => getThemeById(appTheme), [appTheme]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTeacherId, setSelectedTeacherId] = useState('SEMUA');
  const [selectedClass, setSelectedClass] = useState('SEMUA');
  const [selectedSubject, setSelectedSubject] = useState('SEMUA');
  const [selectedStatus, setSelectedStatus] = useState('SEMUA');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('SEMUA');
  const [selectedYear, setSelectedYear] = useState('SEMUA');
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('table');

  // Checkbox Selection State for Bulk Delete
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false);

  // Modal States
  const [detailAgenda, setDetailAgenda] = useState<AgendaEntry | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [agendaToDelete, setAgendaToDelete] = useState<AgendaEntry | null>(null);

  // Extract unique subjects
  const subjectsList = useMemo(() => {
    const list = new Set<string>();
    teachers.forEach((t) => {
      if (t.subject) list.add(t.subject.trim());
    });
    agendas.forEach((a) => {
      if (a.subject) list.add(a.subject.trim());
    });
    return Array.from(list).sort();
  }, [teachers, agendas]);

  // Extract unique years
  const availableYears = useMemo(() => {
    const setY = new Set<string>();
    agendas.forEach((a) => {
      if (a.date) {
        const y = a.date.split('-')[0];
        if (y) setY.add(y);
      }
    });
    return Array.from(setY).sort((a, b) => b.localeCompare(a));
  }, [agendas]);

  // Filtered Agendas
  const filteredAgendas = useMemo(() => {
    return agendas.filter((a) => {
      // Search
      if (searchQuery.trim() !== '') {
        const q = searchQuery.toLowerCase();
        const matchTopic = (a.topic || '').toLowerCase().includes(q);
        const matchTeacher = (a.teacherName || '').toLowerCase().includes(q);
        const matchSubject = (a.subject || '').toLowerCase().includes(q);
        const matchClass = (a.class || '').toLowerCase().includes(q);
        const matchNotes = (a.notes || '').toLowerCase().includes(q);
        const matchTp = (a.tpDescription || '').toLowerCase().includes(q);
        if (!matchTopic && !matchTeacher && !matchSubject && !matchClass && !matchNotes && !matchTp) {
          return false;
        }
      }

      // Teacher
      if (selectedTeacherId !== 'SEMUA') {
        if (a.teacherId) {
          if (a.teacherId !== selectedTeacherId) return false;
        } else {
          // fallback by matching teacher name if teacherId missing
          const targetTeacher = teachers.find((t) => t.id === selectedTeacherId);
          if (targetTeacher && a.teacherName !== targetTeacher.name) return false;
        }
      }

      // Class
      if (selectedClass !== 'SEMUA') {
        if ((a.class || '').trim().toUpperCase() !== selectedClass.trim().toUpperCase()) {
          return false;
        }
      }

      // Subject
      if (selectedSubject !== 'SEMUA') {
        if ((a.subject || '').trim().toLowerCase() !== selectedSubject.trim().toLowerCase()) {
          return false;
        }
      }

      // Status
      if (selectedStatus !== 'SEMUA') {
        if (a.status !== selectedStatus) return false;
      }

      // Date Range (Rentang Tanggal)
      if (startDate && a.date < startDate) return false;
      if (endDate && a.date > endDate) return false;

      // Month
      if (selectedMonth !== 'SEMUA' && a.date) {
        const month = parseInt(a.date.split('-')[1], 10);
        if (month.toString() !== selectedMonth) return false;
      }

      // Year
      if (selectedYear !== 'SEMUA' && a.date) {
        const year = a.date.split('-')[0];
        if (year !== selectedYear) return false;
      }

      return true;
    }).sort((a, b) => {
      const dateCompare = new Date(b.date).getTime() - new Date(a.date).getTime();
      if (dateCompare !== 0) return dateCompare;
      return (b.startPeriod || 0) - (a.startPeriod || 0);
    });
  }, [
    agendas,
    teachers,
    searchQuery,
    selectedTeacherId,
    selectedClass,
    selectedSubject,
    selectedStatus,
    startDate,
    endDate,
    selectedMonth,
    selectedYear,
  ]);

  // Bulk Selection Handlers
  const isAllFilteredSelected = useMemo(() => {
    if (filteredAgendas.length === 0) return false;
    return filteredAgendas.every((a) => selectedIds.includes(a.id));
  }, [filteredAgendas, selectedIds]);

  const toggleSelectAll = () => {
    if (isAllFilteredSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredAgendas.map((a) => a.id));
    }
  };

  const toggleSelectOne = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleBulkDeleteConfirm = () => {
    if (onDeleteAgenda && selectedIds.length > 0) {
      selectedIds.forEach((id) => {
        onDeleteAgenda(id);
      });
      setSelectedIds([]);
      setIsBulkDeleteModalOpen(false);
    }
  };

  // Overview Statistics
  const stats = useMemo(() => {
    const total = agendas.length;
    const teacherIdsWithAgenda = new Set(agendas.map((a) => a.teacherId || a.teacherName).filter(Boolean));
    const activeTeachersCount = teacherIdsWithAgenda.size;
    const completedCount = agendas.filter((a) => a.status === 'Selesai').length;
    const docCount = agendas.filter((a) => a.documentationUrl && a.documentationUrl.trim() !== '').length;

    return {
      total,
      activeTeachersCount,
      completedCount,
      docCount,
    };
  }, [agendas]);

  const handleResetFilters = () => {
    setSearchQuery('');
    setSelectedTeacherId('SEMUA');
    setSelectedClass('SEMUA');
    setSelectedSubject('SEMUA');
    setSelectedStatus('SEMUA');
    setStartDate('');
    setEndDate('');
    setSelectedMonth('SEMUA');
    setSelectedYear('SEMUA');
    setSelectedIds([]);
  };

  // Export to Excel
  const handleExportExcel = async () => {
    if (filteredAgendas.length === 0) {
      alert('Tidak ada data agenda untuk diexport.');
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Agenda Guru');

    // Title Block
    worksheet.mergeCells('A1:J1');
    worksheet.getCell('A1').value = (schoolSettings.schoolName || 'SMPN 1').toUpperCase();
    worksheet.getCell('A1').font = { bold: true, size: 14 };
    worksheet.getCell('A1').alignment = { horizontal: 'center' };

    worksheet.mergeCells('A2:J2');
    worksheet.getCell('A2').value = 'REKAPITULASI AGENDA MENGAJAR GURU';
    worksheet.getCell('A2').font = { bold: true, size: 12 };
    worksheet.getCell('A2').alignment = { horizontal: 'center' };

    worksheet.mergeCells('A3:J3');
    worksheet.getCell('A3').value = `Tanggal Cetak: ${new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}`;
    worksheet.getCell('A3').font = { italic: true, size: 10 };
    worksheet.getCell('A3').alignment = { horizontal: 'center' };

    worksheet.addRow([]);

    // Header Row
    const headerRow = worksheet.addRow([
      'No',
      'Tanggal',
      'Guru Pengajar',
      'Mata Pelajaran',
      'Kelas',
      'Jam ke-',
      'Materi / Topik Pembelajaran',
      'Status',
      'Dokumentasi',
      'Catatan / Refleksi',
    ]);

    headerRow.eachCell((cell) => {
      cell.font = { bold: true, color: { argb: 'FFFFFF' } };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: '4F46E5' }, // Indigo-600
      };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
      };
    });

    // Data Rows
    filteredAgendas.forEach((item, index) => {
      const jam = item.startPeriod === item.endPeriod ? `${item.startPeriod}` : `${item.startPeriod}-${item.endPeriod}`;
      const row = worksheet.addRow([
        index + 1,
        item.date,
        item.teacherName || '-',
        item.subject || '-',
        item.class || '-',
        jam,
        item.topic || '-',
        item.status || '-',
        item.documentationUrl ? 'Ada' : 'Tidak Ada',
        item.notes || '-',
      ]);

      row.eachCell((cell, colNumber) => {
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' },
        };
        if ([1, 2, 5, 6, 8, 9].includes(colNumber)) {
          cell.alignment = { horizontal: 'center', vertical: 'middle' };
        } else {
          cell.alignment = { vertical: 'middle' };
        }
      });
    });

    // Auto Column Width
    worksheet.columns.forEach((column) => {
      let maxLen = 12;
      column.eachCell?.({ includeEmpty: false }, (cell) => {
        const str = String(cell.value || '');
        if (str.length > maxLen) maxLen = Math.min(str.length, 40);
      });
      column.width = maxLen + 4;
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Rekap_Agenda_Guru_${new Date().toISOString().split('T')[0]}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Print Report
  const handlePrint = () => {
    if (filteredAgendas.length === 0) {
      alert('Tidak ada data agenda untuk dicetak.');
      return;
    }

    const todayStr = new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Mohon izinkan pop-up peramban untuk mencetak laporan.');
      return;
    }

    const rowsHtml = filteredAgendas.map((item, idx) => `
      <tr>
        <td style="text-align: center;">${idx + 1}</td>
        <td style="text-align: center; whitespace: nowrap;">${item.date}</td>
        <td><strong>${item.teacherName || '-'}</strong></td>
        <td>${item.subject || '-'}</td>
        <td style="text-align: center;">${item.class || '-'}</td>
        <td style="text-align: center;">${item.startPeriod === item.endPeriod ? item.startPeriod : `${item.startPeriod}-${item.endPeriod}`}</td>
        <td>${item.topic || '-'}</td>
        <td style="text-align: center;">
          <span style="padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: bold; background-color: ${
            item.status === 'Selesai' ? '#dcfce7' : item.status === 'Belum Selesai' ? '#fef9c3' : '#fee2e2'
          }; color: ${
            item.status === 'Selesai' ? '#166534' : item.status === 'Belum Selesai' ? '#854d0e' : '#991b1b'
          };">
            ${item.status}
          </span>
        </td>
        <td>${item.notes || '-'}</td>
      </tr>
    `).join('');

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Laporan Agenda Guru</title>
        <style>
          body { font-family: 'Times New Roman', serif; padding: 24px; color: #1e293b; }
          .kop-container { text-align: center; border-bottom: 3px double #0f172a; padding-bottom: 12px; margin-bottom: 20px; }
          .kop-title { font-size: 18px; font-weight: bold; text-transform: uppercase; margin: 0; }
          .kop-sub { font-size: 14px; margin: 4px 0 0 0; }
          .kop-address { font-size: 11px; color: #475569; margin-top: 2px; }
          .report-header { text-align: center; margin-bottom: 20px; }
          .report-header h2 { font-size: 15px; margin: 0; text-decoration: underline; text-transform: uppercase; }
          .report-header p { font-size: 12px; color: #475569; margin: 4px 0 0 0; }
          table { width: 100%; border-collapse: collapse; font-size: 12px; margin-bottom: 30px; }
          th, td { border: 1px solid #94a3b8; padding: 6px 8px; vertical-align: top; }
          th { background-color: #f1f5f9; font-weight: bold; text-align: center; }
          .signature-section { display: flex; justify-content: space-between; margin-top: 40px; font-size: 12px; page-break-inside: avoid; }
          .signature-box { text-align: center; width: 220px; }
          .signature-space { height: 60px; }
          @media print {
            body { padding: 0; }
            @page { size: A4 landscape; margin: 15mm; }
          }
        </style>
      </head>
      <body>
        <div class="kop-container">
          <p class="kop-title">${schoolSettings.schoolName || 'SMP NEGERI 1'}</p>
          <p class="kop-sub">REKAPITULASI AGENDA GURU MENGAJAR</p>
          <p class="kop-address">${schoolSettings.address || ''}</p>
        </div>

        <div class="report-header">
          <h2>LAPORAN KEGIATAN AGENDA HARIAN GURU</h2>
          <p>Dicetak pada tanggal: ${todayStr} | Total Records: ${filteredAgendas.length}</p>
        </div>

        <table>
          <thead>
            <tr>
              <th style="width: 30px;">No</th>
              <th style="width: 80px;">Tanggal</th>
              <th style="width: 140px;">Nama Guru</th>
              <th style="width: 110px;">Mata Pelajaran</th>
              <th style="width: 50px;">Kelas</th>
              <th style="width: 50px;">Jam</th>
              <th>Materi / Topik Pembelajaran</th>
              <th style="width: 90px;">Status</th>
              <th>Catatan / Refleksi</th>
            </tr>
          </thead>
          <tbody>
            ${rowsHtml}
          </tbody>
        </table>

        <div class="signature-section">
          <div class="signature-box">
            <p>Mengetahui,</p>
            <p>Kepala Sekolah</p>
            <div class="signature-space"></div>
            <p><strong>${schoolSettings.principalName || '....................................'}</strong></p>
            <p>NIP. ${schoolSettings.principalNip || '....................................'}</p>
          </div>
          <div class="signature-box">
            <p>${schoolSettings.address ? schoolSettings.address.split(',')[0] : 'Kota'}, ${todayStr}</p>
            <p>Administrator Sistem</p>
            <div class="signature-space"></div>
            <p><strong>Admin Kurikulum</strong></p>
          </div>
        </div>

        <script>
          window.onload = function() {
            window.print();
          };
        </script>
      </body>
      </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  return (
    <div className="space-y-6 pb-12 animate-fade-in">
      {/* Page Header */}
      <div className={`flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-gradient-to-br ${currentTheme.cardGradients[0] || 'from-indigo-900 via-indigo-800 to-slate-900'} p-6 rounded-3xl text-white shadow-xl relative overflow-hidden`}>
        <div className="absolute -right-10 -bottom-10 opacity-10 pointer-events-none">
          <BookMarked size={220} />
        </div>
        <div className="relative z-10 space-y-1">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/20 backdrop-blur-md text-xs font-semibold text-white border border-white/30">
            <User size={14} /> Panel Administrasi
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">Agenda Guru</h1>
          <p className="text-sm text-white/80 max-w-xl">
            Pemantauan harian, rekapitulasi, dan laporan agenda mengajar seluruh guru dari basis data sekolah.
          </p>
        </div>

        {/* Action Buttons */}
        <div className="relative z-10 flex flex-wrap items-center gap-2">
          <button
            onClick={handleExportExcel}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-semibold text-xs shadow-md transition-all active:scale-95"
            title="Export ke Excel"
          >
            <FileSpreadsheet size={16} /> Export Excel
          </button>
          <button
            onClick={handlePrint}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-slate-700 hover:bg-slate-600 text-white font-semibold text-xs shadow-md transition-all active:scale-95"
            title="Cetak Laporan PDF"
          >
            <Printer size={16} /> Cetak Laporan
          </button>
        </div>
      </div>

      {/* Stats Summary Widgets */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-slate-800/90 p-4 rounded-2xl border border-slate-200/80 dark:border-slate-700/80 shadow-sm flex items-center gap-4">
          <div className="p-3 rounded-xl bg-indigo-50 dark:bg-indigo-950/50 text-indigo-600 dark:text-indigo-400">
            <BookMarked size={24} />
          </div>
          <div>
            <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Total Agenda</p>
            <p className="text-xl font-black text-slate-800 dark:text-white">{stats.total}</p>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800/90 p-4 rounded-2xl border border-slate-200/80 dark:border-slate-700/80 shadow-sm flex items-center gap-4">
          <div className="p-3 rounded-xl bg-sky-50 dark:bg-sky-950/50 text-sky-600 dark:text-sky-400">
            <User size={24} />
          </div>
          <div>
            <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Guru Aktif</p>
            <p className="text-xl font-black text-slate-800 dark:text-white">{stats.activeTeachersCount}</p>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800/90 p-4 rounded-2xl border border-slate-200/80 dark:border-slate-700/80 shadow-sm flex items-center gap-4">
          <div className="p-3 rounded-xl bg-emerald-50 dark:bg-emerald-950/50 text-emerald-600 dark:text-emerald-400">
            <CheckCircle2 size={24} />
          </div>
          <div>
            <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Agenda Selesai</p>
            <p className="text-xl font-black text-slate-800 dark:text-white">{stats.completedCount}</p>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800/90 p-4 rounded-2xl border border-slate-200/80 dark:border-slate-700/80 shadow-sm flex items-center gap-4">
          <div className="p-3 rounded-xl bg-amber-50 dark:bg-amber-950/50 text-amber-600 dark:text-amber-400">
            <ImageIcon size={24} />
          </div>
          <div>
            <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Ada Dokumentasi</p>
            <p className="text-xl font-black text-slate-800 dark:text-white">{stats.docCount}</p>
          </div>
        </div>
      </div>

      {/* Filter and Control Panel */}
      <div className="bg-white dark:bg-slate-800/90 p-5 rounded-2xl border border-slate-200/80 dark:border-slate-700/80 shadow-sm space-y-4">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          {/* Search Bar */}
          <div className="relative flex-1">
            <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Cari guru, topik, mata pelajaran, kelas, atau catatan..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 text-slate-800 dark:text-white placeholder-slate-400"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
              >
                <X size={16} />
              </button>
            )}
          </div>

          {/* View Switcher & Reset */}
          <div className="flex items-center gap-2">
            <div className="bg-slate-100 dark:bg-slate-900 p-1 rounded-xl flex items-center gap-1 border border-slate-200 dark:border-slate-700">
              <button
                onClick={() => setViewMode('table')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  viewMode === 'table'
                    ? 'bg-white dark:bg-slate-800 text-indigo-600 dark:text-indigo-400 shadow-sm'
                    : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'
                }`}
              >
                <List size={15} /> Tabel
              </button>
              <button
                onClick={() => setViewMode('grid')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  viewMode === 'grid'
                    ? 'bg-white dark:bg-slate-800 text-indigo-600 dark:text-indigo-400 shadow-sm'
                    : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'
                }`}
              >
                <LayoutGrid size={15} /> Grid
              </button>
            </div>

            <button
              onClick={handleResetFilters}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-slate-600 dark:text-slate-300 hover:text-indigo-600 dark:hover:text-indigo-400 bg-slate-100 dark:bg-slate-700/60 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-xl transition-all"
            >
              <RotateCcw size={14} /> Reset
            </button>
          </div>
        </div>

        {/* Filters Grid including Rentang Tanggal & Bulan */}
        <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-8 gap-3 pt-3 border-t border-slate-100 dark:border-slate-700/60">
          {/* Teacher Select */}
          <div>
            <label className="block text-[11px] font-bold text-slate-500 dark:text-slate-400 mb-1 uppercase tracking-wider">
              Guru
            </label>
            <select
              value={selectedTeacherId}
              onChange={(e) => setSelectedTeacherId(e.target.value)}
              className="w-full bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700 rounded-xl px-2.5 py-2 text-xs text-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="SEMUA">Semua Guru</option>
              {teachers.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>

          {/* Class Select */}
          <div>
            <label className="block text-[11px] font-bold text-slate-500 dark:text-slate-400 mb-1 uppercase tracking-wider">
              Kelas
            </label>
            <select
              value={selectedClass}
              onChange={(e) => setSelectedClass(e.target.value)}
              className="w-full bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700 rounded-xl px-2.5 py-2 text-xs text-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="SEMUA">Semua Kelas</option>
              {classes.map((cls) => (
                <option key={cls} value={cls}>
                  Kelas {cls}
                </option>
              ))}
            </select>
          </div>

          {/* Subject Select */}
          <div>
            <label className="block text-[11px] font-bold text-slate-500 dark:text-slate-400 mb-1 uppercase tracking-wider">
              Mata Pelajaran
            </label>
            <select
              value={selectedSubject}
              onChange={(e) => setSelectedSubject(e.target.value)}
              className="w-full bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700 rounded-xl px-2.5 py-2 text-xs text-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="SEMUA">Semua Mapel</option>
              {subjectsList.map((sub) => (
                <option key={sub} value={sub}>
                  {sub}
                </option>
              ))}
            </select>
          </div>

          {/* Status Select */}
          <div>
            <label className="block text-[11px] font-bold text-slate-500 dark:text-slate-400 mb-1 uppercase tracking-wider">
              Status
            </label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="w-full bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700 rounded-xl px-2.5 py-2 text-xs text-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="SEMUA">Semua Status</option>
              <option value="Selesai">Selesai</option>
              <option value="Belum Selesai">Belum Selesai</option>
              <option value="Jam kosong">Jam Kosong</option>
            </select>
          </div>

          {/* Rentang Tgl: Mulai */}
          <div>
            <label className="block text-[11px] font-bold text-slate-500 dark:text-slate-400 mb-1 uppercase tracking-wider">
              Mulai Tanggal
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700 rounded-xl px-2 py-1.5 text-xs text-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          {/* Rentang Tgl: Sampai */}
          <div>
            <label className="block text-[11px] font-bold text-slate-500 dark:text-slate-400 mb-1 uppercase tracking-wider">
              Sampai Tanggal
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700 rounded-xl px-2 py-1.5 text-xs text-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          {/* Month Select */}
          <div>
            <label className="block text-[11px] font-bold text-slate-500 dark:text-slate-400 mb-1 uppercase tracking-wider">
              Bulan
            </label>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="w-full bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700 rounded-xl px-2.5 py-2 text-xs text-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="SEMUA">Semua Bulan</option>
              <option value="1">Januari</option>
              <option value="2">Februari</option>
              <option value="3">Maret</option>
              <option value="4">April</option>
              <option value="5">Mei</option>
              <option value="6">Juni</option>
              <option value="7">Juli</option>
              <option value="8">Agustus</option>
              <option value="9">September</option>
              <option value="10">Oktober</option>
              <option value="11">November</option>
              <option value="12">Desember</option>
            </select>
          </div>

          {/* Year Select */}
          <div>
            <label className="block text-[11px] font-bold text-slate-500 dark:text-slate-400 mb-1 uppercase tracking-wider">
              Tahun
            </label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              className="w-full bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700 rounded-xl px-2.5 py-2 text-xs text-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="SEMUA">Semua Tahun</option>
              {availableYears.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Floating Bulk Action Bar when items are selected */}
      {selectedIds.length > 0 && (
        <div className="bg-slate-900 text-white p-4 rounded-2xl shadow-xl flex flex-col sm:flex-row items-center justify-between gap-3 animate-fade-in border border-slate-700">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center font-bold text-xs">
              {selectedIds.length}
            </div>
            <span className="text-xs font-semibold">
              Agenda terpilih untuk tindakan massal
            </span>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setSelectedIds([])}
              className="px-3 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 font-semibold text-xs transition-colors"
            >
              Batal Pilih
            </button>
            {onDeleteAgenda && (
              <button
                onClick={() => setIsBulkDeleteModalOpen(true)}
                className="flex items-center gap-1.5 px-4 py-1.5 rounded-xl bg-rose-600 hover:bg-rose-500 text-white font-semibold text-xs shadow-md transition-all"
              >
                <Trash2 size={14} /> Hapus Terpilih ({selectedIds.length})
              </button>
            )}
          </div>
        </div>
      )}

      {/* Main Results Display */}
      {filteredAgendas.length === 0 ? (
        <div className="bg-white dark:bg-slate-800/90 rounded-3xl p-12 text-center border border-slate-200/80 dark:border-slate-700/80 shadow-sm max-w-lg mx-auto my-8 space-y-3">
          <div className="w-16 h-16 bg-slate-100 dark:bg-slate-700 text-slate-400 rounded-2xl flex items-center justify-center mx-auto">
            <BookMarked size={32} />
          </div>
          <h3 className="text-lg font-bold text-slate-800 dark:text-white">Tidak Ada Agenda Ditemukan</h3>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Tidak ada agenda guru yang cocok dengan kriteria filter atau pencarian Anda. Coba ubah atau reset filter.
          </p>
          <button
            onClick={handleResetFilters}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-semibold text-xs transition-all shadow-md mt-2"
          >
            <RotateCcw size={14} /> Reset Filter
          </button>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filteredAgendas.map((item) => {
            const isSelected = selectedIds.includes(item.id);
            return (
              <div
                key={item.id}
                className={`bg-white dark:bg-slate-800/90 rounded-2xl border p-5 shadow-sm hover:shadow-md transition-all flex flex-col justify-between group relative ${
                  isSelected
                    ? 'border-indigo-500 ring-2 ring-indigo-500/20 dark:ring-indigo-500/30'
                    : 'border-slate-200/80 dark:border-slate-700/80'
                }`}
              >
                <div className="space-y-3">
                  {/* Top Badge Info & Checkbox */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2.5">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelectOne(item.id)}
                        className="w-4 h-4 text-indigo-600 rounded border-slate-300 focus:ring-indigo-500 cursor-pointer"
                      />
                      <div className="w-9 h-9 rounded-xl bg-indigo-100 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 font-bold flex items-center justify-center text-sm shadow-inner">
                        {item.teacherName ? item.teacherName.charAt(0).toUpperCase() : 'G'}
                      </div>
                      <div className="flex flex-col">
                        <select
                          value={item.teacherId || item.teacherName || ''}
                          onChange={(e) => {
                            const val = e.target.value;
                            const selectedTeacher = teachers.find(t => t.id === val || t.name === val);
                            const updatedAgenda = {
                              ...item,
                              teacherId: selectedTeacher ? selectedTeacher.id : '',
                              teacherName: selectedTeacher ? selectedTeacher.name : val,
                            };
                            onUpdateAgenda?.(updatedAgenda);
                          }}
                          className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 hover:border-indigo-500 dark:hover:border-indigo-500 rounded-lg px-2 py-0.5 text-xs font-bold text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-indigo-500 max-w-[150px] truncate"
                        >
                          <option value="" className="text-slate-500">-- Pilih Guru --</option>
                          {teachers.map(t => (
                            <option key={t.id} value={t.id} className="text-slate-800 dark:text-slate-200">{t.name}</option>
                          ))}
                        </select>
                        <p className="text-[11px] font-medium text-slate-500 dark:text-slate-400 mt-0.5">
                          {item.subject || 'Mapel Umum'}
                        </p>
                      </div>
                    </div>

                    {/* Status Badge */}
                    <span
                      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold ${
                        item.status === 'Selesai'
                          ? 'bg-emerald-100 dark:bg-emerald-950/80 text-emerald-700 dark:text-emerald-300'
                          : item.status === 'Belum Selesai'
                          ? 'bg-amber-100 dark:bg-amber-950/80 text-amber-700 dark:text-amber-300'
                          : 'bg-rose-100 dark:bg-rose-950/80 text-rose-700 dark:text-rose-300'
                      }`}
                    >
                      {item.status === 'Selesai' && <CheckCircle2 size={12} />}
                      {item.status === 'Belum Selesai' && <Clock size={12} />}
                      {item.status === 'Jam kosong' && <AlertCircle size={12} />}
                      {item.status}
                    </span>
                  </div>

                  {/* Class & Date Meta */}
                  <div className="flex items-center gap-2 flex-wrap text-xs text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-900/50 p-2.5 rounded-xl border border-slate-100 dark:border-slate-800">
                    <div className="flex items-center gap-1 font-semibold text-indigo-600 dark:text-indigo-400">
                      <Building2 size={14} /> Kelas {item.class}
                    </div>
                    <span className="text-slate-300 dark:text-slate-700">•</span>
                    <div className="flex items-center gap-1">
                      <Calendar size={14} /> {item.date}
                    </div>
                    <span className="text-slate-300 dark:text-slate-700">•</span>
                    <div className="flex items-center gap-1 font-mono font-medium">
                      Jam {item.startPeriod === item.endPeriod ? item.startPeriod : `${item.startPeriod}-${item.endPeriod}`}
                    </div>
                  </div>

                  {/* Topic / Content */}
                  <div>
                    <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1">
                      Topik / Materi Harian
                    </p>
                    <p className="text-xs font-semibold text-slate-800 dark:text-slate-100 line-clamp-3 leading-relaxed">
                      {item.topic || 'Tidak ada topik dicatat.'}
                    </p>
                  </div>

                  {/* Documentation Image Thumbnail */}
                  {item.documentationUrl && (
                    <div className="relative rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 group/img bg-slate-100 dark:bg-slate-900 aspect-video flex items-center justify-center">
                      <img
                        src={item.documentationUrl}
                        alt="Dokumentasi Agenda"
                        className="w-full h-full object-cover transition-transform group-hover/img:scale-105"
                        loading="lazy"
                      />
                      <button
                        onClick={() => setPreviewImage(item.documentationUrl!)}
                        className="absolute inset-0 bg-black/40 opacity-0 group-hover/img:opacity-100 transition-opacity flex items-center justify-center text-white text-xs font-bold gap-1.5 backdrop-blur-xs"
                      >
                        <Eye size={16} /> Pratinjau Foto
                      </button>
                    </div>
                  )}
                </div>

                {/* Bottom Footer Actions */}
                <div className="pt-4 mt-3 border-t border-slate-100 dark:border-slate-700/60 flex items-center justify-between">
                  <button
                    onClick={() => setDetailAgenda(item)}
                    className="inline-flex items-center gap-1 text-xs font-bold text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors"
                  >
                    <Eye size={14} /> Lihat Detail Penuh
                  </button>

                  {onDeleteAgenda && (
                    <button
                      onClick={() => setAgendaToDelete(item)}
                      className="p-1.5 text-slate-400 hover:text-rose-600 dark:hover:text-rose-400 rounded-lg hover:bg-rose-50 dark:hover:bg-rose-950/50 transition-colors"
                      title="Hapus Agenda Ini"
                    >
                      <Trash2 size={15} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* Table View */
        <div className="bg-white dark:bg-slate-800/90 rounded-2xl border border-slate-200/80 dark:border-slate-700/80 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-xs">
              <thead>
                <tr className="bg-slate-50 dark:bg-slate-900/80 text-slate-500 dark:text-slate-400 font-bold border-b border-slate-200 dark:border-slate-700 uppercase tracking-wider text-[11px]">
                  <th className="py-3.5 px-4 text-center w-10">
                    <input
                      type="checkbox"
                      checked={isAllFilteredSelected}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 text-indigo-600 rounded border-slate-300 focus:ring-indigo-500 cursor-pointer"
                      title="Pilih Semua Tanggal/Agenda"
                    />
                  </th>
                  <th className="py-3.5 px-3 text-center">No</th>
                  <th className="py-3.5 px-4">Tanggal & Jam</th>
                  <th className="py-3.5 px-4">Guru</th>
                  <th className="py-3.5 px-4">Mapel</th>
                  <th className="py-3.5 px-4 text-center">Kelas</th>
                  <th className="py-3.5 px-4">Topik Pembelajaran</th>
                  <th className="py-3.5 px-4 text-center">Status</th>
                  <th className="py-3.5 px-4 text-center">Dokumentasi</th>
                  <th className="py-3.5 px-4 text-center">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700/60 text-slate-700 dark:text-slate-200">
                {filteredAgendas.map((item, idx) => {
                  const isSelected = selectedIds.includes(item.id);
                  return (
                    <tr
                      key={item.id}
                      className={`hover:bg-slate-50/80 dark:hover:bg-slate-700/40 transition-colors ${
                        isSelected ? 'bg-indigo-50/40 dark:bg-indigo-950/20' : ''
                      }`}
                    >
                      <td className="py-3 px-4 text-center">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelectOne(item.id)}
                          className="w-4 h-4 text-indigo-600 rounded border-slate-300 focus:ring-indigo-500 cursor-pointer"
                        />
                      </td>
                      <td className="py-3 px-3 text-center font-medium text-slate-400">{idx + 1}</td>
                      <td className="py-3 px-4 whitespace-nowrap">
                        <div className="font-bold text-slate-800 dark:text-white">{item.date}</div>
                        <div className="text-[10px] text-slate-400">
                          Jam ke-{item.startPeriod === item.endPeriod ? item.startPeriod : `${item.startPeriod}-${item.endPeriod}`}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <select
                          value={item.teacherId || item.teacherName || ''}
                          onChange={(e) => {
                            const val = e.target.value;
                            const selectedTeacher = teachers.find(t => t.id === val || t.name === val);
                            const updatedAgenda = {
                              ...item,
                              teacherId: selectedTeacher ? selectedTeacher.id : '',
                              teacherName: selectedTeacher ? selectedTeacher.name : val,
                            };
                            onUpdateAgenda?.(updatedAgenda);
                          }}
                          className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 hover:border-indigo-500 dark:hover:border-indigo-500 rounded-lg px-2 py-1 text-xs font-semibold text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-indigo-500 max-w-[180px] truncate"
                        >
                          <option value="" className="text-slate-500">-- Pilih Guru --</option>
                          {teachers.map(t => (
                            <option key={t.id} value={t.id} className="text-slate-800 dark:text-slate-200">{t.name}</option>
                          ))}
                        </select>
                      </td>
                      <td className="py-3 px-4 text-slate-600 dark:text-slate-300">{item.subject || '-'}</td>
                      <td className="py-3 px-4 text-center font-bold text-indigo-600 dark:text-indigo-400">
                        {item.class}
                      </td>
                      <td className="py-3 px-4 max-w-xs">
                        <p className="line-clamp-2 font-medium">{item.topic || '-'}</p>
                      </td>
                      <td className="py-3 px-4 text-center whitespace-nowrap">
                        <span
                          className={`inline-block px-2.5 py-1 rounded-full text-[10px] font-bold ${
                            item.status === 'Selesai'
                              ? 'bg-emerald-100 dark:bg-emerald-950/80 text-emerald-700 dark:text-emerald-300'
                              : item.status === 'Belum Selesai'
                              ? 'bg-amber-100 dark:bg-amber-950/80 text-amber-700 dark:text-amber-300'
                              : 'bg-rose-100 dark:bg-rose-950/80 text-rose-700 dark:text-rose-300'
                          }`}
                        >
                          {item.status}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center whitespace-nowrap">
                        {item.documentationUrl ? (
                          <button
                            onClick={() => setPreviewImage(item.documentationUrl!)}
                            className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-600 dark:text-amber-400 hover:underline"
                          >
                            <ImageIcon size={13} /> Lihat Foto
                          </button>
                        ) : (
                          <span className="text-slate-400 text-[11px]">-</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-center whitespace-nowrap">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => setDetailAgenda(item)}
                            className="p-1.5 text-indigo-600 hover:bg-indigo-50 dark:text-indigo-400 dark:hover:bg-indigo-950/50 rounded-lg transition-colors"
                            title="Detail Agenda"
                          >
                            <Eye size={16} />
                          </button>
                          {onDeleteAgenda && (
                            <button
                              onClick={() => setAgendaToDelete(item)}
                              className="p-1.5 text-rose-600 hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-950/50 rounded-lg transition-colors"
                              title="Hapus Agenda"
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Detail Agenda Modal */}
      {detailAgenda && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4 animate-fade-in">
          <div className="bg-white dark:bg-slate-800 rounded-3xl max-w-2xl w-full p-6 space-y-5 shadow-2xl border border-slate-200 dark:border-slate-700 max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-start justify-between gap-4 border-b border-slate-100 dark:border-slate-700 pb-4">
              <div>
                <span className="text-[10px] font-black uppercase text-indigo-600 dark:text-indigo-400 tracking-wider">
                  Rincian Agenda Mengajar
                </span>
                <h3 className="text-lg font-bold text-slate-800 dark:text-white mt-0.5">
                  {detailAgenda.teacherName || 'Guru Tanpa Nama'}
                </h3>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {detailAgenda.subject || 'Mata Pelajaran'} • Kelas {detailAgenda.class}
                </p>
              </div>
              <button
                onClick={() => setDetailAgenda(null)}
                className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Content */}
            <div className="space-y-4 text-xs">
              {/* Meta Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 bg-slate-50 dark:bg-slate-900/60 p-3.5 rounded-2xl border border-slate-100 dark:border-slate-800">
                <div>
                  <span className="text-[10px] font-bold text-slate-400 uppercase">Tanggal</span>
                  <p className="font-semibold text-slate-800 dark:text-white mt-0.5">{detailAgenda.date}</p>
                </div>
                <div>
                  <span className="text-[10px] font-bold text-slate-400 uppercase">Jam Pelajaran</span>
                  <p className="font-semibold text-slate-800 dark:text-white mt-0.5">
                    Jam ke-{detailAgenda.startPeriod === detailAgenda.endPeriod ? detailAgenda.startPeriod : `${detailAgenda.startPeriod}-${detailAgenda.endPeriod}`}
                  </p>
                </div>
                <div>
                  <span className="text-[10px] font-bold text-slate-400 uppercase">Status</span>
                  <p className="font-semibold text-slate-800 dark:text-white mt-0.5">{detailAgenda.status}</p>
                </div>
              </div>

              {/* Topic & Activity Type */}
              <div>
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Topik Utama / Materi</span>
                <p className="text-sm font-bold text-slate-800 dark:text-white mt-1 leading-relaxed bg-slate-50 dark:bg-slate-900/40 p-3 rounded-xl border border-slate-100 dark:border-slate-800">
                  {detailAgenda.topic || '-'}
                </p>
              </div>

              {/* TP Description */}
              {detailAgenda.tpDescription && (
                <div>
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Tujuan Pembelajaran (TP)</span>
                  <p className="text-xs font-medium text-slate-700 dark:text-slate-300 mt-1 bg-slate-50 dark:bg-slate-900/40 p-3 rounded-xl border border-slate-100 dark:border-slate-800">
                    {detailAgenda.tpCode ? `[${detailAgenda.tpCode}] ` : ''}{detailAgenda.tpDescription}
                  </p>
                </div>
              )}

              {/* Notes */}
              {detailAgenda.notes && (
                <div>
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Catatan / Refleksi Guru</span>
                  <p className="text-xs italic text-slate-600 dark:text-slate-300 mt-1 bg-amber-50/50 dark:bg-amber-950/30 p-3 rounded-xl border border-amber-200/50 dark:border-amber-900/50">
                    "{detailAgenda.notes}"
                  </p>
                </div>
              )}

              {/* Material Link */}
              {detailAgenda.materialLink && (
                <div>
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Tautan Bahan Ajar</span>
                  <div className="mt-1">
                    <a
                      href={detailAgenda.materialLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 font-semibold hover:underline"
                    >
                      <ExternalLink size={14} /> Buka Tautan Materi
                    </a>
                  </div>
                </div>
              )}

              {/* Documentation Image */}
              {detailAgenda.documentationUrl && (
                <div>
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Foto Dokumentasi Pembelajaran</span>
                  <div className="mt-2 rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-900 max-h-64 flex items-center justify-center">
                    <img
                      src={detailAgenda.documentationUrl}
                      alt="Dokumentasi"
                      className="max-h-64 w-auto object-contain cursor-pointer"
                      onClick={() => setPreviewImage(detailAgenda.documentationUrl!)}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="pt-4 border-t border-slate-100 dark:border-slate-700 flex justify-end">
              <button
                onClick={() => setDetailAgenda(null)}
                className="px-5 py-2.5 rounded-xl bg-slate-800 dark:bg-slate-700 text-white text-xs font-semibold hover:bg-slate-700 dark:hover:bg-slate-600 transition-colors"
              >
                Tutup
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Full Preview Modal */}
      {previewImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-fade-in">
          <div className="relative max-w-4xl w-full max-h-[90vh] flex flex-col items-center justify-center">
            <button
              onClick={() => setPreviewImage(null)}
              className="absolute -top-10 right-0 text-white/80 hover:text-white p-2 rounded-full hover:bg-white/10 transition-colors"
            >
              <X size={24} />
            </button>
            <img
              src={previewImage}
              alt="Foto Dokumentasi Penuh"
              className="max-h-[85vh] max-w-full object-contain rounded-2xl shadow-2xl border border-white/10"
            />
          </div>
        </div>
      )}

      {/* Single Delete Confirmation Modal */}
      {agendaToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4 animate-fade-in">
          <div className="bg-white dark:bg-slate-800 rounded-3xl max-w-md w-full p-6 space-y-4 shadow-2xl border border-slate-200 dark:border-slate-700">
            <div className="w-12 h-12 bg-rose-100 dark:bg-rose-950/60 text-rose-600 dark:text-rose-400 rounded-2xl flex items-center justify-center mx-auto">
              <AlertCircle size={24} />
            </div>
            <div className="text-center space-y-1">
              <h3 className="text-base font-bold text-slate-800 dark:text-white">Hapus Agenda Guru?</h3>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Apakah Anda yakin ingin menghapus agenda mengajar guru <strong>{agendaToDelete.teacherName}</strong> (Kelas {agendaToDelete.class}, Tanggal {agendaToDelete.date})? Tindakan ini tidak dapat dibatalkan.
              </p>
            </div>
            <div className="flex items-center gap-3 pt-2">
              <button
                onClick={() => setAgendaToDelete(null)}
                className="flex-1 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
              >
                Batal
              </button>
              <button
                onClick={() => {
                  if (onDeleteAgenda && agendaToDelete) {
                    onDeleteAgenda(agendaToDelete.id);
                  }
                  setAgendaToDelete(null);
                }}
                className="flex-1 py-2.5 rounded-xl bg-rose-600 hover:bg-rose-500 text-white text-xs font-semibold transition-colors shadow-md"
              >
                Hapus
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      {isBulkDeleteModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4 animate-fade-in">
          <div className="bg-white dark:bg-slate-800 rounded-3xl max-w-md w-full p-6 space-y-4 shadow-2xl border border-slate-200 dark:border-slate-700">
            <div className="w-12 h-12 bg-rose-100 dark:bg-rose-950/60 text-rose-600 dark:text-rose-400 rounded-2xl flex items-center justify-center mx-auto">
              <Trash2 size={24} />
            </div>
            <div className="text-center space-y-1">
              <h3 className="text-base font-bold text-slate-800 dark:text-white">
                Hapus {selectedIds.length} Agenda Sekaligus?
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Apakah Anda yakin ingin menghapus <strong>{selectedIds.length}</strong> entri agenda mengajar guru yang telah dipilih secara bersamaan? Tindakan ini tidak dapat dibatalkan.
              </p>
            </div>
            <div className="flex items-center gap-3 pt-2">
              <button
                onClick={() => setIsBulkDeleteModalOpen(false)}
                className="flex-1 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
              >
                Batal
              </button>
              <button
                onClick={handleBulkDeleteConfirm}
                className="flex-1 py-2.5 rounded-xl bg-rose-600 hover:bg-rose-500 text-white text-xs font-semibold transition-colors shadow-md"
              >
                Ya, Hapus Semua ({selectedIds.length})
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAgendaView;
